import { Achievement } from '../types';

// Define all available achievements
const ACHIEVEMENTS: Omit<Achievement, 'unlocked' | 'unlockedAt'>[] = [
  // ========== HUNTER RANK ACHIEVEMENTS (Level-Based) ==========
  // Primary progression system - tied to player level
  { id: 'rank_civilian', title: 'Civilian', description: 'Start your journey as a regular person', icon: '/icons/civilian-icon.png', image: '/icons/civilian-icon.png', category: 'level', requirement: 1 },
  { id: 'rank_e', title: 'E-Rank Hunter', description: 'Reach level 5 - You\'re no longer a civilian', icon: '🔰', image: '/achievements/ranks/rank_e_hunter.png', category: 'level', requirement: 5 },
  { id: 'rank_d', title: 'D-Rank Hunter', description: 'Reach level 10 - The Association acknowledges your growth', icon: '⚡', image: '/achievements/ranks/rank_d_hunter.png', category: 'level', requirement: 10 },
  { id: 'rank_c', title: 'C-Rank Hunter', description: 'Reach level 20 - Mid-tier hunter status achieved', icon: '🎖️', image: '/achievements/ranks/rank_c_hunter.png', category: 'level', requirement: 20 },
  { id: 'rank_b', title: 'B-Rank Hunter', description: 'Reach level 30 - Among the top 10% of all hunters', icon: '💠', image: '/achievements/ranks/rank_b_hunter.png', category: 'level', requirement: 30 },
  { id: 'rank_a', title: 'A-Rank Hunter', description: 'Reach level 50 - National level power awakens', icon: '💎', image: '/achievements/ranks/rank_a_hunter.png', category: 'level', requirement: 50 },
  { id: 'rank_s', title: 'S-Rank Hunter', description: 'Reach level 75 - Standing among the world\'s elite', icon: '⭐', image: '/achievements/ranks/rank_s_hunter.png', category: 'level', requirement: 75 },
  { id: 'rank_ss', title: 'Shadow Monarch', description: 'Reach level 100 - Transcend all ranks and become a Monarch', icon: '👑', image: '/achievements/ranks/rank_ss_shadow_monarch.png', category: 'level', requirement: 100 },
  
  // ========== QUEST ACHIEVEMENTS ==========
  // Thematic achievements based on quest completion
  { id: 'first_quest', title: 'First Steps', description: 'Complete your first quest and begin your journey', icon: '⚔️', image: '/achievements/quest/quest_awakened.png', category: 'quest', requirement: 1 },
  { id: 'quest_10', title: 'Dungeon Crawler', description: 'Complete 10 quests - The grind begins', icon: '🗡️', image: '/achievements/quest/quest_dungeon_crawler.png', category: 'quest', requirement: 10 },
  { id: 'quest_50', title: 'Gate Breaker', description: 'Complete 50 quests - No gate can stop you', icon: '🏹', image: '/achievements/quest/quest_gate_breaker.png', category: 'quest', requirement: 50 },
  { id: 'quest_100', title: 'Raid Commander', description: 'Complete 100 quests - You lead the charge', icon: '🎯', image: '/achievements/quest/quest_raid_commander.png', category: 'quest', requirement: 100 },
  { id: 'quest_250', title: 'Dungeon Master', description: 'Complete 250 quests - Gates fear your presence', icon: '🏆', image: '/achievements/quest/quest_dungeon_master.png', category: 'quest', requirement: 250 },
  { id: 'quest_500', title: 'System\'s Champion', description: 'Complete 500 quests - The System\'s chosen warrior', icon: '⚜️', image: '/achievements/quest/quest_systems_champion.png', category: 'quest', requirement: 500 },
  
  // ========== STREAK ACHIEVEMENTS ==========
  // Daily dedication and consistency
  { id: 'streak_3', title: 'Daily Grind Begins', description: 'Maintain a 3-day streak - The training never stops', icon: '🔥', image: '/achievements/streak/streak_daily_grind_begins.png', category: 'streak', requirement: 3 },
  { id: 'streak_7', title: 'Weekly Warrior', description: 'Maintain a 7-day streak - Consistency builds power', icon: '📅', image: '/achievements/streak/streak_weekly_warrior.png', category: 'streak', requirement: 7 },
  { id: 'streak_30', title: 'Hunter\'s Discipline', description: 'Maintain a 30-day streak - The System rewards dedication', icon: '🎴', image: '/achievements/streak/streak_hunters_discipline.png', category: 'streak', requirement: 30 },
  { id: 'streak_100', title: 'Relentless Champion', description: 'Maintain a 100-day streak - Nothing can stop you', icon: '💫', image: '/achievements/streak/streak_relentless_champion.png', category: 'streak', requirement: 100 },
  { id: 'streak_365', title: 'Eternal Hunter', description: 'Maintain a 365-day streak - A year of unwavering resolve', icon: '🌟', image: '/achievements/streak/streak_eternal_hunter.png', category: 'streak', requirement: 365 },
  
  // ========== STAT ACHIEVEMENTS ==========
  // Power progression milestones
  { id: 'stat_100', title: 'Mana Awakening', description: 'Reach 100 total stats - Your power crystallizes', icon: '📈', image: '/achievements/stats/stat_mana_awakening.png', category: 'stat', requirement: 100 },
  { id: 'stat_250', title: 'Transcendent Force', description: 'Reach 250 total stats - Break mortal limitations', icon: '💥', image: '/achievements/stats/stat_transcendent_force.png', category: 'stat', requirement: 250 },
  { id: 'stat_500', title: 'Monarch\'s Vessel', description: 'Reach 500 total stats - A body fit for a ruler', icon: '💪', image: '/achievements/stats/stat_monarchs_vessel.png', category: 'stat', requirement: 500 },
  { id: 'stat_1000', title: 'Superhuman Power', description: 'Reach 1000 total stats - Transcend human limits', icon: '✨', category: 'stat', requirement: 1000 },
  
  // ========== SPECIAL ACHIEVEMENTS ==========
  // Quest Mastery
  { id: 'daily_master', title: 'Perfect Raid Day', description: 'Complete all daily quests in one day - Flawless execution', icon: '🌅', image: '/achievements/special/special_perfect_raid_day.png', category: 'special', requirement: 1 },
  { id: 'boss_first', title: 'Red Gate Survivor', description: 'Complete your first boss quest - Face true danger', icon: '👹', image: '/achievements/special/special_red_gate_survivo.png', category: 'special', requirement: 1 },
  { id: 'boss_10', title: 'Boss Slayer', description: 'Complete 10 boss quests - Fear is your weapon', icon: '🗡️', image: '/achievements/special/special_boss_slayer.png', category: 'special', requirement: 10 },
  
  // Passive Skill Mastery
  { id: 'passive_first', title: 'Foundation Training', description: 'Create your first passive skill - Begin daily cultivation', icon: '🎯', image: '/achievements/special/special_foundation_training.png', category: 'special', requirement: 1 },
  { id: 'passive_skill_5', title: 'Parallel Growth', description: 'Create 5 passive skills - Multiple paths to power', icon: '🔱', image: '/achievements/special/special_parallel_growth.png', category: 'special', requirement: 5 },
  { id: 'passive_skill_10', title: 'Complete Arsenal', description: 'Create 10 passive skills - A hunter needs many tools', icon: '⚔️', image: '/achievements/special/special_complete_arsenal.png', category: 'special', requirement: 10 },
  { id: 'passive_streak_30', title: 'Unwavering Path', description: 'Reach a 30-day streak on any passive skill - Dedication shows', icon: '🛡️', image: '/achievements/special/special_unwavering_path.png', category: 'special', requirement: 30 },
  { id: 'passive_streak_100', title: 'Iron Will Training', description: 'Reach a 100-day streak on any passive skill - Legendary persistence', icon: '🔥', image: '/achievements/special/special_iron_will_training.png', category: 'special', requirement: 100 },
  { id: 'passive_streak_365', title: 'Eternal Cultivation', description: 'Reach a 365-day streak on any passive skill - A year of mastery', icon: '👑', image: '/achievements/special/special_eternal_cultivation.png', category: 'special', requirement: 365 },
  { id: 'passive_level_10', title: 'Peak Performance', description: 'Reach level 10 on any passive skill - Ultimate refinement', icon: '💠', category: 'special', requirement: 10 },
  { id: 'passive_max_5', title: 'Pentacle Master', description: 'Max out 5 passive skills to level 10 - Five pillars of strength', icon: '💎', category: 'special', requirement: 5 },
  { id: 'passive_boost_10', title: 'System Enhancement', description: 'Have at least +10% total passive boosts - Permanent power up', icon: '⚡', category: 'special', requirement: 10 },

  // Goal Completion Achievements
  { id: 'passive_goal_first', title: 'First Victory', description: 'Complete your first goal duration - Small wins lead to big changes', icon: '🎯', category: 'special', requirement: 1 },
  { id: 'passive_goal_5', title: 'Habit Champion', description: 'Complete 5 goal durations - You are becoming unstoppable', icon: '🏆', category: 'special', requirement: 5 },
  { id: 'passive_goal_30_day', title: 'Month Master', description: 'Complete a 30-day goal - A full month of dedication', icon: '📅', category: 'special', requirement: 1 },
  { id: 'passive_goal_perfect', title: 'Perfect Execution', description: 'Complete a goal with 100% consistency - Flawless discipline', icon: '💎', category: 'special', requirement: 1 },
];

export const achievementService = {
  // Get initial achievements for a new user (all locked except Civilian)
  getInitialAchievements: (): Achievement[] => {
    return ACHIEVEMENTS.map(ach => ({
      ...ach,
      unlocked: ach.id === 'rank_civilian', // Pre-unlock Civilian achievement
      unlockedAt: ach.id === 'rank_civilian' ? Date.now() : null,
    }));
  },

  // Merge existing achievements with new ones (for when new achievements are added)
  mergeAchievements: (existingAchievements: Achievement[]): Achievement[] => {
    const existingIds = existingAchievements.map(a => a.id);
    const newAchievements = ACHIEVEMENTS.filter(a => !existingIds.includes(a.id)).map(ach => ({
      ...ach,
      unlocked: ach.id === 'rank_civilian', // Pre-unlock Civilian achievement
      unlockedAt: ach.id === 'rank_civilian' ? Date.now() : null,
    }));
    return [...existingAchievements, ...newAchievements];
  },

  // Check and unlock achievements based on criteria
  checkAchievements: (
    currentAchievements: Achievement[],
    questsCompleted: number,
    level: number,
    streak: number,
    totalStats: number,
    dailyQuestsCompleted: number,
    dailyQuestsTotal: number,
    bossQuestsCompleted: number
  ): { updatedAchievements: Achievement[]; newlyUnlocked: Achievement[] } => {
    const newlyUnlocked: Achievement[] = [];
    
    // Create a copy to avoid mutations
    const updatedAchievements = currentAchievements.map(achievement => {
      if (achievement.unlocked) return achievement;

      let shouldUnlock = false;

      switch (achievement.category) {
        case 'quest':
          shouldUnlock = questsCompleted >= achievement.requirement;
          break;
        case 'level':
          shouldUnlock = level >= achievement.requirement;
          break;
        case 'streak':
          shouldUnlock = streak >= achievement.requirement;
          break;
        case 'stat':
          shouldUnlock = totalStats >= achievement.requirement;
          break;
        case 'special':
          if (achievement.id === 'daily_master') {
            shouldUnlock = dailyQuestsTotal > 0 && dailyQuestsCompleted >= dailyQuestsTotal;
          } else if (achievement.id === 'boss_first') {
            shouldUnlock = bossQuestsCompleted >= 1;
          } else if (achievement.id === 'boss_10') {
            shouldUnlock = bossQuestsCompleted >= 10;
          }
          break;
      }

      if (shouldUnlock) {
        const unlockedAchievement = {
          ...achievement,
          unlocked: true,
          unlockedAt: Date.now(),
        };
        newlyUnlocked.push(unlockedAchievement);
        return unlockedAchievement;
      }

      return achievement;
    });

    return { updatedAchievements, newlyUnlocked };
  },

  // Get unlocked achievements
  getUnlockedAchievements: (achievements: Achievement[]): Achievement[] => {
    return achievements.filter(a => a.unlocked);
  },

  // Get locked achievements
  getLockedAchievements: (achievements: Achievement[]): Achievement[] => {
    return achievements.filter(a => !a.unlocked);
  },

  // Get achievement progress
  getAchievementProgress: (achievements: Achievement[], achievementId: string, currentValue: number): number => {
    const achievement = achievements.find(a => a.id === achievementId);
    if (!achievement) return 0;
    return Math.min((currentValue / achievement.requirement) * 100, 100);
  },

  // Check passive skill achievements
  checkPassiveSkillAchievements: (
    currentAchievements: Achievement[],
    totalSkills: number,
    maxStreak: number,
    maxLevel: number,
    maxedSkillsCount: number,
    totalPassiveBoostPercent: number,
    goalCompletions?: {
      totalGoalsCompleted: number;
      thirtyDayGoalsCompleted: number;
      perfectGoalsCompleted: number;
    }
  ): { updatedAchievements: Achievement[]; newlyUnlocked: Achievement[] } => {
    const newlyUnlocked: Achievement[] = [];
    
    const updatedAchievements = currentAchievements.map((achievement) => {
      if (achievement.unlocked) return achievement;

      let shouldUnlock = false;

      switch (achievement.id) {
        case 'passive_first':
          shouldUnlock = totalSkills >= 1;
          break;
        case 'passive_skill_5':
          shouldUnlock = totalSkills >= 5;
          break;
        case 'passive_skill_10':
          shouldUnlock = totalSkills >= 10;
          break;
        case 'passive_streak_30':
          shouldUnlock = maxStreak >= 30;
          break;
        case 'passive_streak_100':
          shouldUnlock = maxStreak >= 100;
          break;
        case 'passive_streak_365':
          shouldUnlock = maxStreak >= 365;
          break;
        case 'passive_level_10':
          shouldUnlock = maxLevel >= 10;
          break;
        case 'passive_max_5':
          shouldUnlock = maxedSkillsCount >= 5;
          break;
        case 'passive_boost_10':
          shouldUnlock = totalPassiveBoostPercent >= 10;
          break;
        case 'passive_goal_first':
          // First goal completed
          shouldUnlock = goalCompletions ? goalCompletions.totalGoalsCompleted >= 1 : false;
          break;
        case 'passive_goal_5':
          // 5 goals completed
          shouldUnlock = goalCompletions ? goalCompletions.totalGoalsCompleted >= 5 : false;
          break;
        case 'passive_goal_30_day':
          // Completed a 30-day goal
          shouldUnlock = goalCompletions ? goalCompletions.thirtyDayGoalsCompleted >= 1 : false;
          break;
        case 'passive_goal_perfect':
          // Completed a goal with 100% consistency (no missed days)
          shouldUnlock = goalCompletions ? goalCompletions.perfectGoalsCompleted >= 1 : false;
          break;
      }

      if (shouldUnlock) {
        const unlockedAchievement = {
          ...achievement,
          unlocked: true,
          unlockedAt: Date.now(),
        };
        newlyUnlocked.push(unlockedAchievement);
        return unlockedAchievement;
      }

      return achievement;
    });

    return { updatedAchievements, newlyUnlocked };
  },
};

